import { Component, OnInit } from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormGroup,
    Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { first } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/AuthService';
import { SmtpService } from 'src/app/Services/smtp.service';
import swal from 'sweetalert2';

@Component({
  selector: 'pm-mail-config',
  templateUrl: './mail-config.component.html',
  styleUrls: ['./mail-config.component.css']
})
export class MailConfigComponent implements OnInit {

    smtpForm!: FormGroup;

    // These do not need to be displayed in the UI
    // readonly startTlsEnabled = true;
    // readonly sslEnabled = false;
    readonly active = true;
    smtpId: number | null = null;
    isUpdateMode = false;
    isLoading = false;
    originalCredentialType: string | null = null;
    mailProviderOptions = [
    {
        value: 'MICROSOFT_365',
        viewValue: 'Microsoft 365'
    },
    {
        value: 'OUTLOOK',
        viewValue: 'Outlook'
    },
    {
        value: 'GMAIL',
        viewValue: 'Gmail'
    }
    ];

  constructor(
        private fb: FormBuilder,
        private smtpService: SmtpService,
        private authService: AuthService,
        private router: Router
  ) {
    this.smtpForm = this.fb.group({

        mailProvider: [
            null,
            Validators.required
        ],

        credentialType: [
            'SMTP_USERNAME_PASSWORD',
            Validators.required
        ],

        username: [
            null
        ],

        password: [
            null
        ],

        tenantId: [
            null
        ],

        oauthClientId: [
            null
        ],

        clientSecret: [
            null
        ],

        fromEmail: [
            null,
            [
                Validators.required,
                Validators.email
            ]
        ],

        fromName: [
            null,
            Validators.required
        ],

        emails: this.fb.array([
            this.createEmailControl()
        ])

    });
   }

  ngOnInit(): void {
    this.getSmtpDetails();
  }

    getSmtpDetails(): void {

    const clientId = this.authService.getClientId();

    if (!clientId) {
        return;
    }

    this.isLoading = true;

    this.smtpService
        .getSmtpDetails(clientId)
        .pipe(first())
        .subscribe({

            next: (response: any) => {

                this.isLoading = false;

                if (!response) {
                    this.isUpdateMode = false;
                    return;
                }

                /*
                 * Configuration already exists.
                 */
                this.isUpdateMode = true;

                this.smtpId =
                    response.id || null;

                this.originalCredentialType =
                    response.credentialType || null;


                const provider =
                    this.getProviderFromResponse(response);


                this.smtpForm.patchValue({

                    mailProvider:
                        provider,

                    credentialType:
                        response.credentialType ||
                        'SMTP_USERNAME_PASSWORD',

                    username:
                        response.username || null,

                    /*
                    * Never return/display stored secrets.
                    */
                    password:
                        null,

                    tenantId:
                        response.tenantId || null,

                    oauthClientId:
                        response.oauthClientId || null,

                    clientSecret:
                        null,

                    fromEmail:
                        response.fromEmail || null,

                    fromName:
                        response.fromName || null

                });


                this.updateCredentialValidators();


                this.emails.clear();

                if (response.emails?.length) {

                    response.emails.forEach(
                        (email: string) => {

                            this.emails.push(
                                this.createEmailControl(email)
                            );

                        }
                    );

                } else {

                    this.emails.push(
                        this.createEmailControl()
                    );

                }

            },

            error: (error) => {

                this.isLoading = false;

                /*
                 * Important:
                 *
                 * If backend returns 404 when no SMTP configuration exists,
                 * we don't want to show an error.
                 *
                 * This simply means user needs to register one.
                 */
                if (error.status === 404) {

                    this.isUpdateMode = false;
                    this.smtpId = null;

                    return;
                }

                this.showError(
                    error?.error?.message ||
                    'Failed to load SMTP configuration.'
                );
            }
        });
    }

    get emails(): FormArray {
        return this.smtpForm.get('emails') as FormArray;
    }
    get selectedProvider(): string {
        return this.smtpForm.get('mailProvider')?.value;
    }

    get selectedCredentialType(): string {
        return this.smtpForm.get('credentialType')?.value;
    }

    get isSmtpCredential(): boolean {
        return this.selectedCredentialType === 'SMTP_USERNAME_PASSWORD';
    }

    get isMicrosoftOAuth(): boolean {
        return this.selectedCredentialType === 'MICROSOFT_OAUTH';
    }    

    private createEmailControl(value: string | null = null) {
        return this.fb.control(
            value,
            [
                Validators.required,
                Validators.email
            ]
        );
    }

    addEmail(): void {
        this.emails.push(this.createEmailControl());
    }

    removeEmail(index: number): void {
        if (this.emails.length > 1) {
            this.emails.removeAt(index);
        }
    }

    onProviderChange(): void {

        const provider = this.selectedProvider;

        if (provider === 'MICROSOFT_365') {

            this.smtpForm
                .get('credentialType')
                ?.setValue(
                    'MICROSOFT_OAUTH',
                    { emitEvent: false }
                );

            this.smtpForm.patchValue({
                username: null,
                password: null
            });

        } else if (
            provider === 'GMAIL' ||
            provider === 'OUTLOOK'
        ) {

            this.smtpForm
                .get('credentialType')
                ?.setValue(
                    'SMTP_USERNAME_PASSWORD',
                    { emitEvent: false }
                );

            this.smtpForm.patchValue({
                tenantId: null,
                oauthClientId: null,
                clientSecret: null
            });
        }

        this.updateCredentialValidators();
    }


    private updateCredentialValidators(): void {

        const username =
            this.smtpForm.get('username');

        const password =
            this.smtpForm.get('password');

        const tenantId =
            this.smtpForm.get('tenantId');

        const oauthClientId =
            this.smtpForm.get('oauthClientId');

        const clientSecret =
            this.smtpForm.get('clientSecret');


        /*
        * Clear previous validators
        */
        username?.clearValidators();
        password?.clearValidators();

        tenantId?.clearValidators();
        oauthClientId?.clearValidators();
        clientSecret?.clearValidators();


        if (this.isSmtpCredential) {

            username?.setValidators([
                Validators.required
            ]);

            /*
            * Password required:
            *
            * CREATE
            * OR
            * switching OAuth -> SMTP
            */
            if (
                !this.isUpdateMode ||
                this.originalCredentialType !== 'SMTP_USERNAME_PASSWORD'
            ) {
                password?.setValidators([
                    Validators.required
                ]);
            }

        }


        if (this.isMicrosoftOAuth) {

            tenantId?.setValidators([
                Validators.required
            ]);

            oauthClientId?.setValidators([
                Validators.required
            ]);

            /*
            * Secret required:
            *
            * CREATE
            * OR
            * switching SMTP -> OAuth
            */
            if (
                !this.isUpdateMode ||
                this.originalCredentialType !== 'MICROSOFT_OAUTH'
            ) {
                clientSecret?.setValidators([
                    Validators.required
                ]);
            }

        }


        username?.updateValueAndValidity({
            emitEvent: false
        });

        password?.updateValueAndValidity({
            emitEvent: false
        });

        tenantId?.updateValueAndValidity({
            emitEvent: false
        });

        oauthClientId?.updateValueAndValidity({
            emitEvent: false
        });

        clientSecret?.updateValueAndValidity({
            emitEvent: false
        });
    }  
    
    private getProviderFromResponse(response: any): string | null {

        // Microsoft OAuth / Microsoft 365
        if (response.tenantId) {
            return 'MICROSOFT_365';
        }

        // Gmail
        if (response.host === 'smtp.gmail.com') {
            return 'GMAIL';
        }

        // Outlook SMTP
        if (response.host === 'smtp.office365.com') {
            return 'OUTLOOK';
        }

        return null;
    }
    
    private getSmtpSettings(
        provider: string
    ): {
        host: string;
        port: number;
        encryptionType: string;
    } {

        switch (provider) {

            case 'GMAIL':

                return {
                    host: 'smtp.gmail.com',
                    port: 587,
                    encryptionType: 'STARTTLS'
                };


            case 'OUTLOOK':

                return {
                    host: 'smtp.office365.com',
                    port: 587,
                    encryptionType: 'STARTTLS'
                };


            case 'MICROSOFT_365':

                return {
                    host: 'smtp.office365.com',
                    port: 587,
                    encryptionType: 'STARTTLS'
                };


            default:

                throw new Error(
                    'Invalid mail provider selected.'
                );
        }
    }

    onRegister(): void {

        this.updateCredentialValidators();

        this.smtpForm.markAllAsTouched();

        if (this.smtpForm.invalid) {
            return;
        }


        const formValue =
            this.smtpForm.getRawValue();


        const reqData: any = {

            clientId:
                this.authService.getClientId(),

            credentialType:
                formValue.credentialType,

            fromEmail:
                formValue.fromEmail?.trim(),

            fromName:
                formValue.fromName?.trim(),

            active:
                true,

            emails:
                formValue.emails
                    .map(
                        (email: string) =>
                            email?.trim()
                    )
                    .filter(
                        (email: string) =>
                            !!email
                    )

        };


        /*
        * SMTP
        */
        if (
            formValue.credentialType ===
            'SMTP_USERNAME_PASSWORD'
        ) {

            const smtpSettings =
                this.getSmtpSettings(
                    formValue.mailProvider
                );


            reqData.host =
                smtpSettings.host;

            reqData.port =
                smtpSettings.port;

            reqData.username =
                formValue.username?.trim();

            reqData.smtpAuth =
                true;

            reqData.encryptionType =
                smtpSettings.encryptionType;


            if (formValue.password?.trim()) {

                reqData.password =
                    formValue.password;

            }

        }


        /*
        * Microsoft OAuth
        */
        if (
            formValue.credentialType ===
            'MICROSOFT_OAUTH'
        ) {

            reqData.tenantId =
                formValue.tenantId?.trim();

            reqData.oauthClientId =
                formValue.oauthClientId?.trim();


            if (formValue.clientSecret?.trim()) {

                reqData.clientSecret =
                    formValue.clientSecret;

            }

        }


        if (this.isUpdateMode) {

            this.updateSmtp(reqData);

        } else {

            this.registerSmtp(reqData);

        }
    }

    private registerSmtp(reqData: any): void {

    this.smtpService
        .registerSmtp(reqData)
        .pipe(first())
        .subscribe({

            next: (response: any) => {

                swal.fire({
                    icon: 'success',
                    text: 'SMTP configuration registered successfully.',
                    showConfirmButton: false,
                    timer: 3000
                });

                /*
                 * Configuration now exists,
                 * therefore load it and switch page
                 * into UPDATE mode.
                 */
                this.getSmtpDetails();

            },

            error: (error) => {

                this.showError(
                    error?.error?.message ||
                    'Unable to register SMTP configuration.'
                );

            }

        });
    }

    private updateSmtp(reqData: any): void {

  if (!this.smtpId) {
    this.showError('SMTP configuration ID not found.');
    return;
  }

  this.smtpService
    .updateSmtp(
      this.smtpId,
      reqData
    )
    .pipe(first())
    .subscribe({

      next: (response: any) => {

        swal.fire({
          icon: 'success',
          text: 'SMTP configuration updated successfully.',
          showConfirmButton: false,
          timer: 3000
        });

        this.getSmtpDetails();
      },

      error: (error) => {

        this.showError(
          error?.error?.message ||
          'Unable to update SMTP configuration.'
        );
      }
    });
    }

    onCancel(): void {
        this.router.navigate(['/home']);
    }

    private showError(message: string): void {

        swal.fire({
            title: 'Error!',
            icon: 'error',
            text: message,
            showConfirmButton: false,
            timer: 5000
        });
    }

}
